import * as React from "react";
import { FC, useCallback, useEffect, useState } from "react";
import { Feature } from "ol";
import { Collapsible } from "./Collapsible";
import { GamePointStatus } from "../Map/enums";

export interface IDemoGame {
    gameMode: boolean;
    gameList: Feature[];
    selectedGame: Feature;
    approachedGameCheckPoints: Feature[];
    leftGameCheckPoints: Feature[];
    gameCompleted: Feature;
    selectedCheckPoint: Feature;
    onStartGameMode: (simulatorMode: boolean) => void;
    onStopGameMode: () => void;
    onSelectGame: (gameId: string) => void;
    onStartGame: () => void;
    onStopGame: () => void;
    onSetFound: (feature) => void;
    onSelectCheckPoint: (checkPointId: string) => void;
}

export const DemoGame: FC<IDemoGame> = ({
    gameMode,
    gameList,
    selectedGame,
    approachedGameCheckPoints,
    leftGameCheckPoints,
    gameCompleted,
    selectedCheckPoint,
    onStartGameMode,
    onStopGameMode,
    onSelectGame,
    onStartGame,
    onStopGame,
    onSetFound,
    onSelectCheckPoint
}: IDemoGame) => {
    let gameId: string;
    const [gameStarted, setGameStarted] = useState<boolean>(false);
    const [isSimulator, setIsSimulator] = useState<boolean>(true);

    useEffect(() => {
        !gameMode && setGameStarted(false);
    }, [gameMode]);

    const onStart = () => {
        setGameStarted(true);
        onStartGame();
    };
    const onStop = () => {
        setGameStarted(false);
        onStopGame();
    };

    useEffect(() => {
        !selectedGame && setGameStarted(false);
    }, [selectedGame]);

    return (
        <Collapsible title="Spēle" defaultCollapsed={false}>
            <div className="list">
                <div className="list-item">
                    <input
                        type="checkbox"
                        id={"show-game-map"}
                        title={"show-game-map"}
                        defaultChecked={false}
                        onChange={(e) => (e.target.checked ? onStartGameMode(isSimulator) : onStopGameMode())}
                    ></input>
                    <label htmlFor={"show-game-map"}>{"Spēles režīms"}</label>
                </div>
                <div className="list">
                    <div className="list-item">
                        <input
                            type="checkbox"
                            id="game-simulator"
                            title="use-simulator"
                            defaultChecked={true}
                            onChange={(e) => setIsSimulator(e.target.checked)}
                            disabled={gameMode}
                        ></input>
                        <label htmlFor="game-simulator">{"Simulatora režīms"}</label>
                    </div>
                </div>
            </div>
            {gameList ? (
                <div className="list">
                    {gameList.map((game) => (
                        <div className="list-item" key={"spele" + game.get("GAMEID")}>
                            <div>
                                <span>{"spēle ar GAMEID: " + game.get("GAMEID")}</span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
            {gameMode ? (
                <div className="list games">
                    {!gameStarted ? (
                        <>
                            <input
                                type="text"
                                title="atlasīt spēli pēc id"
                                placeholder="atlasīt spēli pēc id"
                                onChange={(e) => (gameId = e.target.value)}
                            />
                            <button title={"select-game"} onClick={() => onSelectGame(gameId)}>
                                Atlasīt spēli
                            </button>
                        </>
                    ) : null}
                    {selectedGame ? (
                        <GameAttributes
                            gameStarted={gameStarted}
                            selectedGame={selectedGame}
                            approachedGameCheckPoints={approachedGameCheckPoints}
                            leftGameCheckPoints={leftGameCheckPoints}
                            gameCompleted={gameCompleted}
                            selectedCheckPoint={selectedCheckPoint}
                            onStartGame={onStart}
                            onStopGame={onStop}
                            onSetFound={onSetFound}
                            onSelectCheckPoint={onSelectCheckPoint}
                        ></GameAttributes>
                    ) : null}
                </div>
            ) : null}
        </Collapsible>
    );
};

const GameAttributes = ({
    gameStarted,
    selectedGame,
    approachedGameCheckPoints,
    leftGameCheckPoints,
    gameCompleted,
    selectedCheckPoint,
    onStartGame,
    onStopGame,
    onSetFound,
    onSelectCheckPoint
}: {
    gameStarted: boolean;
    selectedGame: Feature;
    approachedGameCheckPoints: Feature[];
    leftGameCheckPoints: Feature[];
    gameCompleted: Feature;
    selectedCheckPoint: Feature;
    onStartGame: CallableFunction;
    onStopGame: CallableFunction;
    onSetFound: CallableFunction;
    onSelectCheckPoint: CallableFunction;
}) => {
    const [checkPointId, setCheckPointId] = useState<string>();
    const [approached, setApproached] = useState<Feature[]>([]);

    useEffect(() => {
        if (!approachedGameCheckPoints || !approachedGameCheckPoints.length) {
            return;
        }
        const checkPoints: Feature[] = getApproached();
        approachedGameCheckPoints.map((aa) => !checkPoints.includes(aa) && checkPoints.push(aa));

        setApproached(checkPoints);
    }, [approachedGameCheckPoints]);

    useEffect(() => {
        if (!leftGameCheckPoints || !leftGameCheckPoints.length) {
            return;
        }
        const checkPoints: Feature[] = getApproached();
        const newCheckPoints = [];
        checkPoints.map((bb) => !leftGameCheckPoints.includes(bb) && newCheckPoints.push(bb));
        setApproached(newCheckPoints);
    }, [leftGameCheckPoints]);

    const getApproached = useCallback(() => [...approached], [approached]);

    return (
        <div className="list">
            Izvēlēta spēle: <span>{selectedGame.get("GAMEID")}</span>
            {gameStarted ? (
                <>
                    <button title={"stop-game"} onClick={() => onStopGame()}>
                        Beigt spēli
                    </button>
                    <div className="list">
                        {approached && approached.length
                            ? approached.map((feature) =>
                                  feature.get("GAMEPOINTSTATUS") !== GamePointStatus.Completed ? (
                                      <div className="list-item" key={"check-point-" + feature.get("POINTID")}>
                                          <div>
                                              <span>{"spēles punkts: " + feature.get("POINTID")}</span>
                                              <button title={"atrasts"} onClick={() => onSetFound(feature)}>
                                                  Atzīmēt `Atrasts`
                                              </button>
                                          </div>
                                      </div>
                                  ) : null
                              )
                            : null}
                    </div>
                </>
            ) : !gameCompleted ? (
                <button title={"start-game"} onClick={() => onStartGame(selectedGame)}>
                    Sākt spēli
                </button>
            ) : gameCompleted ? (
                <div className="list">Spēle pabeigta!</div>
            ) : null}
            <div className="list">
                <input
                    type="text"
                    title="atlasīt kontrolpunktu pēc id"
                    placeholder="atlasīt kontrolpunktu pēc id"
                    onChange={(e) => setCheckPointId(e.target.value)}
                />
                <button title={"select-checkpoint"} onClick={() => onSelectCheckPoint(checkPointId)}>
                    Atlasīt k.p.
                </button>
            </div>
            {selectedCheckPoint ? (
                <div className="list-item" key={"selected-check-point-" + selectedCheckPoint.get("POINTID")}>
                    <div>
                        <span>{"kontrolpunkts: " + selectedCheckPoint.get("POINTID")}</span>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
